// The device's own English voice for the copilot's read-aloud replies.
//
// UpliftAI's voices are Urdu only and read English in an Urdu accent with
// Urdu numbers. When the restaurant has no English server voice (Google TTS),
// POST /ai/tts hands an English reply back as text and the browser reads it
// with a plain English voice from the device (Web Speech API).

/** English voices in the order we prefer them. */
const ENGLISH_ORDER = ['en-us', 'en-gb', 'en-in', 'en-au']

const isEnglish = (v: Pick<SpeechSynthesisVoice, 'lang'>) => v.lang.toLowerCase().replace('_', '-').startsWith('en')

/**
 * The voice to read English with: the device's default voice when it is
 * English, else US, British, Indian or Australian English, else any English
 * voice. Undefined (no English voice listed yet) lets the browser pick by
 * the utterance's lang.
 */
export function pickEnglishVoice<V extends Pick<SpeechSynthesisVoice, 'lang' | 'default'>>(voices: V[]): V | undefined {
  const english = voices.filter(isEnglish)
  const byDefault = english.find((v) => v.default)
  if (byDefault) return byDefault
  for (const lang of ENGLISH_ORDER) {
    const v = english.find((x) => x.lang.toLowerCase().replace('_', '-') === lang)
    if (v) return v
  }
  return english[0]
}

/**
 * The text cut into pieces of at most `max` characters, at sentence ends
 * where it can, then at commas, then between words. Chrome stops a single
 * utterance after about 15 seconds, so a reply is read as a queue of pieces.
 */
export function speechChunks(text: string, max = 220): string[] {
  const pieces: string[] = []
  let cur = ''
  const add = (part: string) => {
    const p = part.trim()
    if (!p) return
    if (!cur) cur = p
    else if (cur.length + 1 + p.length <= max) cur += ` ${p}`
    else {
      pieces.push(cur)
      cur = p
    }
  }
  for (const sentence of text.split(/(?<=[.!?])\s+|\n+/)) {
    if (sentence.length <= max) {
      add(sentence)
      continue
    }
    for (const clause of sentence.split(/(?<=[,;:])\s+/)) {
      if (clause.length <= max) {
        add(clause)
        continue
      }
      for (let word of clause.split(/\s+/)) {
        while (word.length > max) {
          add(word.slice(0, max))
          word = word.slice(max)
        }
        add(word)
      }
    }
  }
  if (cur) pieces.push(cur)
  return pieces
}

/**
 * Reads text aloud with the device's English voice. `start` fires when the
 * first piece starts, `end` once when the last one ends or reading fails.
 * Returns a stop function, or null when the device can't speak (no Web Speech
 * API, as in an Android WebView) or there is nothing to read.
 */
export function speakOnDevice(text: string, lang: string, on: { start: () => void; end: () => void }): (() => void) | null {
  const synth = typeof window !== 'undefined' ? window.speechSynthesis : undefined
  if (!synth || typeof SpeechSynthesisUtterance === 'undefined') return null
  const pieces = speechChunks(text)
  if (!pieces.length) return null
  synth.cancel()
  const voice = pickEnglishVoice(synth.getVoices())
  let done = false
  const finish = () => {
    if (done) return
    done = true
    on.end()
  }
  pieces.forEach((piece, i) => {
    const u = new SpeechSynthesisUtterance(piece)
    u.lang = voice?.lang ?? (lang === 'en' ? 'en-US' : lang)
    if (voice) u.voice = voice
    if (i === 0) u.onstart = () => !done && on.start()
    if (i === pieces.length - 1) u.onend = finish
    u.onerror = finish
    synth.speak(u)
  })
  return () => {
    done = true
    synth.cancel()
  }
}
